import dayjs from "dayjs";
import { getChartOption } from "./linesChartOptions.js";
import {
  queryMeterPower,
  getDemands,
  demandTracking,
  queryMaxRealDemand
} from "./api/index.js";
import { download_get } from "@/api/modules/download/index.js";
import { getGroupSettingList } from "../../../tabs/StrategyController/api.js";

const $str = window.$str;

export class EntityLoadTracking {
  constructor(stationId, options) {
    this.stationId = stationId; // 当前电站id
    this.startTime =
      options.startTime ?? dayjs().format("YYYY-MM-DD ") + "00:00:00";
    this.endTime =
      options.endTime ?? dayjs().format("YYYY-MM-DD ") + "23:59:59";
    this.mmdMonth = options.mmdMonth; // [月最大需量]曲线对应的月份

    this.selectOptions = {
      entityMeters: { name: $str.getKey('数据分析/实体电表'), data: [] },
      virtualMeters: { name: $str.getKey('数据分析/虚拟电表'), data: [] },
      datumLines: { name: $str.getKey('数据分析/基准线'), data: [] },
      allDemands: { name: $str.getKey('数据分析/需量'), data: [] }
    };

    this.chartData = {
      entityMeters: [], // 实体表-图表曲线s
      virtualMeters: [], // 虚拟表-图表曲线s
      datumLines: [], // 基准线-图表曲线s
      allDemands: [], // 电站下【demand的总和】【各eLink的demand】
      demandTracks: [] // 多条【eLink运行需量】的曲线
    };
    this.showDemandProtection = false; // 是否展示【需量保护阈值】曲线，由 groupSettingList 决定
  }

  /**
   * 初始化图表
   * @param {*} chartObj
   * @param {*} handleType 0:初始化页面时的初始化图表  1:查询时的刷新图表
   */
  async initCharts(chartObj, handleType = 0) {
    /** 获取图表数据
     * 1、实体电表
     * 2、虚拟电表
     * 3、需量
     * 4、电站下多条eLink运行需量的图表数据 >>> 1-1#eLink运行需量曲线 | 2-1#eLink运行需量曲线
     */
    // 先取分组信息，用于判断【需量保护阈值】曲线/选项是否展示
    await this.getElinkGroupData();
    const p1 = this.getMetersChartData(handleType); // 获取图表的【实体电表&虚拟电表】曲线数据
    const p2 = this.getDemandsChartData(handleType); // 获取图表的【需量】数据
    await Promise.all([p1, p2]).catch(err => {
      console.error($str.getKey('数据分析/负荷跟踪-虚拟表请求图表数据的相关接口-请求异常'), err);
    });
    this.renderCharts(chartObj);
  }

  /**
   * 更新电站id >>> 直接触发所有数据更新&图表刷新
   * @param {*} id
   */
  async updateStationId(id) {
    this.stationId = id;
  }

  /**
   * 更新当前选择的时间范围
   * @param {*} st
   * @param {*} et
   */
  updateTimeRange(st, et) {
    this.startTime = dayjs(st).format("YYYY-MM-DD ") + "00:00:00";
    this.endTime = dayjs(et).format("YYYY-MM-DD ") + "23:59:59";
  }

  /**
   * 更新图表
   * @param {*} chartObj
   */
  async updateCharts(chartObj) {
    await this.initCharts(chartObj, 1);
  }

  /**
   * 执行图表的渲染
   * @param {*} chartObj eChart的实例
   */
  renderCharts(chartObj) {
    // 组件已卸载（图表实例被销毁）时，异步返回的数据不再渲染
    if (!chartObj || chartObj.isDisposed?.()) return;

    // 创建用于图表渲染的配置对象

    const options = getChartOption(
      this.chartData,
      this.startTime,
      this.endTime,
      1
    );
    // 将配置对象设置给eChart进行渲染
    chartObj.clear();
    chartObj.setOption(options);
  }

  /**
   * 更新图例的选中项
   * @param {*} chartObj
   * @param {*} groupKey
   * @param {*} showLineList
   */
  updateChartsLegend(chartObj, groupKey, showLineList) {
    // 更新图表数据的选中状态
    this.chartData[groupKey].forEach(it => {
      if (showLineList.find(line => line.key === it.key)) {
        it.isShow = true;
      } else {
        it.isShow = false;
      }
    });
    // 更新左侧选择面板各项的选中状态
    this.selectOptions[groupKey]?.data.forEach(it => {
      if (showLineList.find(line => line.key === it.key)) {
        it.isShow = true;
      } else {
        it.isShow = false;
      }
    });
    // 更新图表渲染
    this.renderCharts(chartObj);
  }

  /**
   * 获取图表中选中线条的uuid
   * @returns
   */
  getLegendSelectedList() {
    const selectedList = [];
    // 获取图表中选中项的uuid
    Object.values(this.chartData).forEach(group => {
      group.forEach(it => {
        if (it.isShow) {
          selectedList.push(it.key);
        }
      });
    });

    return selectedList;
  }

  /**
   * 更新【月最大需量】曲线
   * @param {*} chartObj
   * @param {*} month
   */
  async refreshMonthlyMaxDemandLine(chartObj, month) {
    this.mmdMonth = month;
    // 请求对应月份需量，更新图表数据，重新渲染图表
    const params = {
      startTime: dayjs(this.mmdMonth).startOf("month").format("YYYY-MM-DD HH:mm:ss"),
      endTime: dayjs(this.mmdMonth).endOf("month").format("YYYY-MM-DD HH:mm:ss"),
      stationId: this.stationId
    };
    const demand = await queryMaxRealDemand(params);
    this.chartData.allDemands.forEach(it => {
      if (it.key === $str.getKey('数据分析/月最大需量')) {
        const monthStr = dayjs(this.mmdMonth).format("YYYY年MM月");
        it.name = `${monthStr}${$str.getKey('数据分析/最大需量')}`;
        it.demand = demand;

        // 更新图表渲染
        this.renderCharts(chartObj);
      }
    });
  }

  /** ======【获取数据的方法】================================================================================================================= */

  /**
   * 获取电表的图标数据-实体电表&虚拟电表&基准线
   * @param {*} handleType 0:初始化页面时-获取数据  1:查询条件更新-查询时的获取数据
   * @returns
   */
  getMetersChartData(handleType = 0) {
    return queryMeterPower(
      this.stationId,
      this.startTime,
      this.endTime,
      []
    ).then(res => {
      if (res?.data && Array.isArray(res.data)) {
        const entityMeters = [];
        const entitylist = [];
        const virtualMeters = [];
        const virtualList = [];
        const datumLines = []; // 基准线
        const datumList = [];
        res.data.forEach(meter => {
          // 实体电表
          if (meter.type == 0) {
            const chartItem = {
              key: meter.uuid,
              name: meter.name,
              data:
                meter.powerList?.map(it => [
                  it.record_time,
                  Number(it.power).toFixed(2)
                ]) ?? [],
              isShow: this.computeLineIsShowForGetData(
                "entityMeters",
                meter.uuid,
                handleType
              )
            };
            const optionItem = {
              key: meter.uuid,
              label: meter.name,
              isShow: this.computeLineIsShowForGetData(
                "entityMeters",
                meter.uuid,
                handleType
              )
            };
            entityMeters.push(chartItem);
            entitylist.push(optionItem);
          }
          // 虚拟电表
          if (meter.type == 1) {
            const chartItem = {
              key: meter.uuid,
              name: meter.name,
              data:
                meter.powerList?.map(it => [
                  it.record_time,
                  Number(it.power).toFixed(2)
                ]) ?? [],
              isShow: this.computeLineIsShowForGetData(
                "virtualMeters",
                meter.uuid,
                handleType
              )
            };
            const optionItem = {
              key: meter.uuid,
              label: meter.name,
              isShow: this.computeLineIsShowForGetData(
                "virtualMeters",
                meter.uuid,
                handleType
              )
            };
            virtualMeters.push(chartItem);
            virtualList.push(optionItem);
          }
          // 基准线
          if (meter.type == 2) {
            const chartItem = {
              key: meter.uuid,
              name: meter.baseline.name,
              data: meter.baseline.value, // 标线对应的数值
              isShow: this.computeLineIsShowForGetData(
                "datumLines",
                meter.uuid,
                handleType
              )
            };
            const optionItem = {
              key: meter.uuid,
              label: meter.baseline.name,
              isShow: this.computeLineIsShowForGetData(
                "datumLines",
                meter.uuid,
                handleType
              ),
              data: meter.baseline.value // 标线对应的数值
            };
            datumLines.push(chartItem);
            datumList.push(optionItem);
          }
        });

        // 实体电表：【echarts & 左侧的选项面板】需要的数据
        this.chartData.entityMeters = entityMeters;
        this.selectOptions.entityMeters.data = entitylist;
        // 虚拟电表：【echarts & 左侧的选项面板】需要的数据
        this.chartData.virtualMeters = virtualMeters;
        this.selectOptions.virtualMeters.data = virtualList;
        // 基准线：【echarts & 左侧的选项面板】需要的数据
        this.chartData.datumLines = datumLines;
        this.selectOptions.datumLines.data = datumList;
      }
    });
  }

  /**
   * 获取需量的数据
   * @param {*} handleType 0:初始化页面时-获取数据  1:查询条件更新-查询时的获取数据
   * @returns
   */
  async getDemandsChartData(handleType = 0) {
    const demandlList = [];
    const allDemands = [];
    // ======[格式化整理"需量"曲线簇]======================================================== //
    await getDemands(this.stationId).then(res => {
      if (res?.data?.elinkDemands && Array.isArray(res.data.elinkDemands)) {
        let total = 0;
        res.data.elinkDemands.forEach(it => {
          const demandItem = {
            name: (it.elinkName || it.unitName || it.elinkId) + $str.getKey('数据分析/需量'),
            demand: it.demand,
            key: it.elinkId,
            // isShow: true,
            isShow: this.computeLineIsShowForGetData(
              "demandItems",
              it.elinkId,
              handleType
            )
          };
          allDemands.push(demandItem);

          const optionItem = {
            label: it.elinkName + $str.getKey('数据分析/需量'),
            key: it.elinkId,
            isShow: this.computeLineIsShowForGetData(
              "demandItems",
              it.elinkId,
              handleType
            )
          };
          demandlList.push(optionItem);

          total += parseFloat(it.demand);
        });

        if (this.showDemandProtection) {
          const demandItem = {
            name: $str.getKey('数据分析/需量保护阈值'),
            demand: total,
            key: res.data.stationId,
            isShow: this.computeLineIsShowForGetData(
              "allDemands",
              res.data.stationId,
              handleType
            )
          };
          allDemands.unshift(demandItem);

          const optionItem = {
            label: $str.getKey('数据分析/需量保护阈值'),
            key: res.data.stationId,
            isShow: this.computeLineIsShowForGetData(
              "allDemands",
              res.data.stationId,
              handleType
            )
          };
          demandlList.unshift(optionItem);
        }
      }
    });

    // ======[添加一条"月最大需量"曲线]======================================================== //
    const params = {
      startTime: dayjs(this.mmdMonth).startOf("month").format("YYYY-MM-DD HH:mm:ss"),
      endTime: dayjs(this.mmdMonth).endOf("month").format("YYYY-MM-DD HH:mm:ss"),
      stationId: this.stationId
    };
    await queryMaxRealDemand(params).then(res => {
      const itemKey = $str.getKey('数据分析/月最大需量');
      const monthStr = dayjs(this.mmdMonth).format("YYYY年MM月");

      const demandItem = {
        name: `${monthStr}${$str.getKey('数据分析/最大需量')}`,
        demand: res,
        key: itemKey,
        isShow: this.computeLineIsShowForGetData(
          "allDemands",
          itemKey,
          handleType
        )
      };
      allDemands.unshift(demandItem);

      const optionItem = {
        label: $str.getKey('数据分析/月最大需量'),
        key: itemKey,
        isShow: this.computeLineIsShowForGetData(
          "allDemands",
          itemKey,
          handleType
        )
      };
      demandlList.unshift(optionItem);
    });

    this.chartData.allDemands = allDemands;
    this.selectOptions.allDemands.data = demandlList;
  }

  /**
   * 获取电站下多条eLink运行需量的数据
   * @param {*} handleType 0:初始化页面时-获取数据  1:查询条件更新-查询时的获取数据
   * @returns
   */
  getDemandTracksChartData(handleType = 0) {
    return demandTracking(this.stationId, this.startTime, this.endTime).then(
      res => {
        if (res?.data && Array.isArray(res.data)) {
          const demandTracks = [];
          const demandTrackList = [];
          res.data.forEach(demandTrack => {
            if (
              demandTrack?.demandTrackingList &&
              Array.isArray(demandTrack.demandTrackingList)
            ) {
              const chartItem = {
                key: demandTrack.uuid,
                name: demandTrack.name + $str.getKey('数据分析/运行需量曲线'),
                data: demandTrack.demandTrackingList.map(p => {
                  return [p.record_time, Number(p.demand).toFixed(2)];
                }),
                isShow: this.computeLineIsShowForGetData(
                  "demandTracks",
                  demandTrack.uuid,
                  handleType
                )
              };
              demandTracks.push(chartItem);
              const optionItem = {
                key: demandTrack.uuid,
                label: demandTrack.name,
                isShow: this.computeLineIsShowForGetData(
                  "demandTracks",
                  demandTrack.uuid,
                  handleType
                )
              };
              demandTrackList.push(optionItem);
            }
          });
          this.chartData.demandTracks = demandTracks;
          this.selectOptions.demandTracks.data = demandTrackList;
        }
      }
    );
  }

  /** ======【辅助性功能方法】================================================================================================================= */

  /**
   * 导出数据
   * @param {*} options
   */
  async exportLoadTrackingData(params) {
    const url = `/dashboard/dataAnalysis/new/queryMeterPower/export`;
    const name = `load_${new Date().getTime()}.xlsx`;
    await download_get(url, params, name)
  }

  /**
   * 计算当前曲线是否展示
   * 初始化时，默认展示所有的 实体表功率曲线 & 需量曲线
   * 搜索或勾选曲线刷新图表时，基于this.selectOptions的勾选状态计算是否展示曲线
   * @param {*} groupKey
   * @param {*} lineKey
   * @param {*} handleType
   * @returns
   */
  computeLineIsShowForGetData(groupKey, lineKey, handleType = 0) {
    // 初始化页面时-获取
    if (handleType == 0) {
      // 实体表
      if (groupKey == "entityMeters") {
        return true;
      }
      // 虚拟表
      if (groupKey == "virtualMeters") {
        return true;
      }
      // 基准线
      if (groupKey == "datumLines") {
        return true;
      }
      // 电站下【demand的总和】【各eLink的demand】
      if (groupKey == "allDemands") {
        if (lineKey == $str.getKey('数据分析/月最大需量')) {
          return false; // 默认隐藏
        }
        return true;
      }
      if (groupKey == "demandItems") {
        return false;
      }
      // 多条【eLink运行需量】的曲线
      if (groupKey == "demandTracks") {
        return false;
      }
    }

    // 更新图表时-获取
    if (handleType == 1) {
      // 实体表
      if (groupKey == "entityMeters") {
        return (
          this.selectOptions[groupKey].data.find(it => it.key == lineKey)
            ?.isShow ?? true
        );
      }
      // 虚拟表
      if (groupKey == "virtualMeters") {
        return (
          this.selectOptions[groupKey].data.find(it => it.key == lineKey)
            ?.isShow ?? true
        );
      }
      // 基准线
      if (groupKey == "datumLines") {
        return (
          this.selectOptions[groupKey].data.find(it => it.key == lineKey)
            ?.isShow ?? true
        );
      }
      // 电站下【demand的总和】【各eLink的demand】
      if (groupKey == "allDemands") {
        return (
          this.selectOptions[groupKey].data.find(it => it.key == lineKey)
            ?.isShow ?? true
        );
      }
      if (groupKey == "demandItems") {
        return (
          this.selectOptions["allDemands"].data.find(it => it.key == lineKey)
            ?.isShow ?? false
        );
      }
      // 多条【eLink运行需量】的曲线
      if (groupKey == "demandTracks") {
        return (
          this.selectOptions[groupKey].data.find(it => it.key == lineKey)
            ?.isShow ?? false
        );
      }
    }
  }

  /**获取elinkgroup数据
   * 空数组 → 无分组策略 → 展示【需量保护阈值】曲线与选项
   * 非空数组 → 存在分组策略 → 不展示【需量保护阈值】曲线与选项
   */
  async getElinkGroupData() {
    try {
      const res = await getGroupSettingList(this.stationId);
      if (res?.data && Array.isArray(res.data)) {
        this.showDemandProtection = res.data.length === 0;
      } else {
        this.showDemandProtection = false;
      }
    } catch (err) {
      console.error('获取elink分组策略失败：', err);
      this.showDemandProtection = false;
    }
  }
}
